/**
 * useScreenOrientation Hook
 * Detects device orientation with fallback for older browsers
 * Returns 'portrait' or 'landscape'
 */

import { useState, useEffect } from 'react';

type Orientation = 'portrait' | 'landscape';

export const useScreenOrientation = (): Orientation => {
  const getOrientation = (): Orientation => {
    // Modern browsers: Use Screen Orientation API
    if (window.screen?.orientation?.type) {
      return window.screen.orientation.type.includes('portrait') 
        ? 'portrait' 
        : 'landscape';
    }
    
    // Fallback: Compare window dimensions
    return window.innerWidth < window.innerHeight 
      ? 'portrait' 
      : 'landscape';
  };

  const [orientation, setOrientation] = useState<Orientation>(getOrientation());

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(getOrientation());
    };

    // Listen to orientation change event (modern browsers)
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange);
    }

    // Listen to resize event (fallback)
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      if (window.screen?.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
      }
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  return orientation;
};
